import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  Avatar,
  useColorModeValue,
  Grid,
  GridItem,
  Icon,
  Spinner,
  useToast,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { FaEdit, FaSave, FaUpload  } from "react-icons/fa";
import { doc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore";
import { uploadBytesResumable, getDownloadURL, ref as sRef } from "firebase/storage";
import { useAuth } from "../../context/AuthProvider";
import { firestore, storage } from "../../firebase/firebaseConfig";
import TrustScoreDisplay from "../../components/TrustScoreDisplay";
import FeedbackSummary from "../../components/FeedbackSummary";
import { fetchUser, fetchUserProfile } from "../../services/userService";
import { useTranslation } from "react-i18next";


const ProfilePage = () => {
  const { t, i18n } = useTranslation();

  const { user } = useAuth(); 

  // State Hooks
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userInfo, setUserInfo] = useState({
    userName: "",
    email: "",
    phoneNumber: "",
    trustScore: "",
  });
  const [pastTrades, setPastTrades] = useState([]);

  // UI Styling Hooks
  const cardBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");
  const accentColor = useColorModeValue("green.500", "green.300");
  const toast = useToast();

  // Fetch User Profile
// Fetch User Profile
useEffect(() => {
  const fetchUserProfileData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Fetch user profile data and user data
      const userProfileData = await fetchUserProfile(user.uid);
      const userData = await fetchUser(user.uid);

      if (userData && userProfileData) {
        // Merge both objects into a single state update
        setUserInfo((prev) => ({
          ...prev,
          ...userData,       // Merge fetched user data
          ...userProfileData // Merge fetched profile data
        }));
      }
    } catch (error) {
      console.error(t("profilepage.profile_fetch_error"), error);
      toast({
        title: t("profilepage.profile_fetch_error_title"),
          description: t("profilepage.profile_fetch_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  fetchUserProfileData();
}, [user, toast]);

  // Fetch Past Trades
  useEffect(() => {
    const fetchPastTrades = async () => {
      if (!user) return;

      try {
        const tradesCollectionRef = collection(firestore, "trades");
        const tradesQuerySnapshot = await getDocs(tradesCollectionRef);

        const completedTrades = tradesQuerySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (trade) =>
              (trade.sellerId === user.uid || trade.buyerId === user.uid) &&
              (trade.status === "completed" || trade.status === "canceled")
          );

        setPastTrades(completedTrades);
      } catch (error) {
        console.error(t("profilepage.trades_fetch_error"), error);
      }
    };

    fetchPastTrades();
  }, [user]);

    // Handle Avatar Upload
    const handleAvatarChange = (e) => {
      if (e.target.files[0]) {
        setAvatarFile(e.target.files[0]);
      }
    };

  // Save Profile Changes
  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {

      let avatarURL = userInfo.photoURL;

      // Upload avatar if a new file is provided
      if (avatarFile) {
        const storageRef = sRef(storage, `avatars/${user.uid}_${Date.now()}`);
        const uploadTask = await uploadBytesResumable(storageRef, avatarFile);
        avatarURL = await getDownloadURL(uploadTask.ref);
      }

      // Update user profile
      const userDocRef = doc(firestore, "users", user.uid);
      await updateDoc(userDocRef, {
        userName: user.userName,
        phone: userInfo.phone,
        photoURL: avatarURL,
      });

      setUserInfo((prev) => ({ ...prev, photoURL: avatarURL }));
      setIsEditing(false);
      toast({
        title: t("profilepage.profile_updated_title"),
        description: t("profilepage.profile_updated_message"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(t("profilepage.profile_update_error"), error);
      toast({
        title: t("profilepage.profile_update_error_title"),
        description: t("profilepage.profile_update_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Input Change Handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box bg="brand.200" color="brand.500" p={8}>
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
        gap={6}
        maxW="6xl"
        mx="auto"
      >
        {/* User Profile Card */}
        <GridItem>
          <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="xl">
            <CardBody>
              <VStack spacing={4} align="center">
              <Avatar size="xl" src={userInfo.photoURL || ""} name={userInfo.displayName} />
                {isEditing && (
                  <>
                    <Input type="file" accept="image/*" onChange={handleAvatarChange} />
                    <Button
                      leftIcon={<FaUpload />}
                      size="sm"
                      colorScheme="teal"
                      onClick={handleSave}
                    >
                      {t("profilepage.upload_avatar_button")}
                      </Button>
                  </>
                )}
                <Heading size="md" color={textColor}>
                  {isEditing ? (
                    <Input
                      name="userName"
                      value={userInfo.userName}
                      onChange={handleInputChange}
                      placeholder={t("profilepage.username_placeholder")}
                      size="md"
                      bg="white"
                    />
                  ) : (
                    userInfo.userName || t("profilepage.default_username")
                  )}
                </Heading>

                <Text color={textColor}>
                {t("profilepage.email_label")}: {userInfo.email}
                </Text>

                <Text color={textColor}>
                {t("profilepage.phone_label")}: {isEditing ? (
                    <Input
                      name="phoneNumber"
                      value={userInfo.phoneNumber}
                      onChange={handleInputChange}
                      placeholder={t("profilepage.phone_placeholder")}
                      size="md"
                      bg="white"
                    />
                  ) : (
                    userInfo.phoneNumber || t("profilepage.not_available")
                  )}
                </Text>

                <Button
                  leftIcon={<Icon as={isEditing ? FaSave : FaEdit} />}
                  size="lg"
                  bg={isEditing ? "green.400" : accentColor}
                  color="white"
                  onClick={isEditing ? handleSave : () => setIsEditing(true)}
                  isLoading={saving}
                >
                  {isEditing ? t("profilepage.save_changes_button") : t("profilepage.edit_profile_button")}
                  </Button>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Trust Score & Feedback Card */}
        <GridItem>
          <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="xl">
            <CardBody>
              <Heading size="md" color={textColor} mb={4}>
              {t("profilepage.performance_section_title")}
              </Heading>
              <TrustScoreDisplay trustScore={userInfo.trustScore} />
              <FeedbackSummary userId={user.uid} />
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Past Trades Section */}
      <Box maxW="6xl" mx="auto" py={8} px={6} bg={cardBgColor} borderRadius="lg" shadow="xl" mt={6}>
        <Heading size="md" mb={4} color={textColor}>
        {t("profilepage.past_trades_section_title")}
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
          {pastTrades.map((trade) => (
            <Box key={trade.id} p={4} bg={useColorModeValue("gray.100", "gray.600")} borderRadius="md" shadow="md">
             <Text fontWeight="bold">{t("profilepage.trade_id_label")}: {trade.id}</Text>
              <Text>{t("profilepage.amount_label")}: {trade.amount}</Text>
              <Text>{t("profilepage.rate_label")}: {trade.rate}</Text>
              <Text>{t("profilepage.status_label")}: {trade.status}</Text>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfilePage;
